import SearchDataGridWithTotalRow from 'components/search/search-data-grid/search-data-grid-with-total-row/search-data-grid-with-total-row.component';
import { useSearchParamsContext } from 'components/search/search.hooks';
import { useMemo } from 'react';
import LoyaltyDataGridToolbar from '../loyalty-data-grid-toolbar/loyalty-data-grid-toolbar.component';
import {
  useExportLocationsTrendsCSVMutation,
  useExportOfferTrendsCSVMutation,
} from 'api/reports-service/reports-service';
import { getFileName } from '../loyalty-performance-report.utils';
import OverPercentageTooltipCellRenderer from '../over-percentage-tooltip-cell-renderer/over-percentage-tooltip-cell-renderer.component';
import { useTranslation } from 'react-i18next';

type TrendsDataGridProps = {
  queryName: string;
  'data-testid': string;
  displayInfoHeaderName: string;
  maxColumns: number;
  isOffer?: boolean;
};

const TrendsDataGrid = ({
  displayInfoHeaderName,
  maxColumns,
  isOffer,
  queryName,
  ...rest
}: TrendsDataGridProps) => {
  const { t } = useTranslation();
  const { searchParams } = useSearchParamsContext();

  const isActiveMemberPercentDatatype =
    searchParams.datatype.value === 'activeMembersPercent';

  const columns = useMemo(
    () => [
      {
        field: 'displayInfoTitle',
        headerName: displayInfoHeaderName,
        hideable: false,
        sortable: false,
        minWidth: 300,
      },
      ...Array.from({ length: maxColumns }).map((_, i) => ({
        field: `granularity${i + 1}`,
        headerName: `${
          searchParams.granularity.options.find(
            op => op.id === searchParams.granularity.value,
          )?.label
        } ${i + 1}`,
        hideable: false,
        sortable: false,
        renderCell: params => (
          <OverPercentageTooltipCellRenderer
            value={params.value}
            title={t(
              'page.loyaltyPerformanceReport.activeMemberPercent.gridTooltip.title',
            )}
            description={t(
              'page.loyaltyPerformanceReport.activeMemberPercent.gridTooltip.description',
            )}
            showTooltip={value =>
              isActiveMemberPercentDatatype &&
              Number(value.replace('%', '')) > 100
            }
          />
        ),
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxColumns, searchParams.granularity.value, searchParams.datatype.value],
  );

  return (
    <SearchDataGridWithTotalRow
      columns={columns}
      initialState={{
        pinnedColumns: { left: ['displayInfoTitle'] },
      }}
      slots={{
        toolbar: LoyaltyDataGridToolbar,
      }}
      slotProps={{
        toolbar: {
          toolbar: {
            export: {
              query: isOffer
                ? useExportOfferTrendsCSVMutation
                : useExportLocationsTrendsCSVMutation,
              fileName: getFileName(
                {
                  startDate: searchParams.startdate.value as string,
                  endDate: searchParams.enddate.value as string,
                  granularity: searchParams.granularity.value as string,
                  dataType: isOffer
                    ? (searchParams.offerdatatype.value as string)
                    : (searchParams.datatype.value as string),
                },
                isOffer ? 'OFFER_TRENDS' : 'LOCATION_TRENDS',
              ),
              queryName,
              filteredSearchParams: [
                'comparisonstartdate',
                'comparisonenddate',
                'count',
                'page',
                'sortBy',
                !isOffer ? 'offerIds' : undefined,
                !isOffer ? 'offerdatatype' : 'datatype',
                'sortDirection',
              ].filter(Boolean),
              includeColumnsField: false,
            },
          },
        },
      }}
      queryName={queryName}
      {...rest}
    />
  );
};

export default TrendsDataGrid;
